import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";
import {
  calculateNights,
  calculateRowGst,
  numberToWords,
} from "./invoiceUtils";
import { API_URL } from "../../Config";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-02-28" (or "2026-02-28T00:00:00.000Z") -> "28 February 2026"
const formatDateLong = (date) => {
  if (!date) return "";
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return date;
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// "2026-02-28" -> "28-02-2026"
const formatDateToDisplay = (date) => {
  if (!date) return "";
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return date;
  return `${day}-${month}-${year}`;
};
// "2026-11-07" -> "2026-11-08" (string-based, so no timezone shifts)
const nextDay = (date) => {
  if (!date) return "";
  const [y, m, d] = String(date).split("T")[0].split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

// Date picker that never changes the input's type on focus (that trick crashes
// Safari). A real <input type="date"> sits invisibly on top of a formatted label,
// so the picker opens natively and the saved value stays "YYYY-MM-DD".
const DateField = ({
  value,
  display,
  placeholder,
  className,
  onChange,
  min,
  wrapperClassName = "w-full min-w-0",
}) => (
  <div className={`relative ${wrapperClassName}`}>
    <span
      className={`block truncate ${className} ${value ? "" : "text-[#b9bdb6]"}`}
    >
      {display || placeholder}
    </span>
    <input
      type="date"
      value={value || ""}
      min={min || undefined}
      onChange={onChange}
      onClick={(e) => {
        try {
          if (e.target.showPicker) e.target.showPicker();
        } catch (_) {
          /* picker already open or unsupported */
        }
      }}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
    />
  </div>
);

// ₹ sign + amount that hug each other: the input is only as wide as its value,
// so the symbol never drifts away from the number.
const MoneyField = ({ value, onChange, className, placeholder = "0000" }) => (
  <span className="inline-flex items-center justify-end">
    <span>₹</span>
    <input
      type="text"
      className={`${className} text-right`}
      style={{ width: `${Math.max(String(value ?? "").length, 4) + 1}ch` }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </span>
);

const emptyRow = () => ({
  particulars: "",
  checkin: "",
  checkout: "",
  nights: "",
  rate: "",
  cgst: "",
  sgst: "",
});

// invoiceid: the invoice to load and edit (falls back to the :invoiceid route param
// so this works whether it's passed as a prop or rendered straight from a route)
// onUpdate: optional callback fired with the server response after a successful save
const InvoiceEdit = ({ invoiceid: invoiceidProp, onUpdate }) => {
  const navigate = useNavigate();
  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [rows, setRows] = useState([emptyRow()]);

  // UI-only toggle: switches between the "with GST" and "without GST" design
  const [includeGst, setIncludeGst] = useState(true);

  const [invoiceno, setinvoiceno] = useState("");
  const [invoicedate, setinvoicedate] = useState("");
  const [guestname, setguestname] = useState("");
  const [gstno, setgstno] = useState("");
  const [country, setcountry] = useState("");
  const [amountinwords, setamountinwords] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [gst, setGst] = useState("");
  const [total, setTotal] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------- Load existing invoice ----------------
  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setLoadError(data.message || "Failed to load invoice.");
          return;
        }

        const inv = data.invoice;
        setinvoiceno(inv.invoiceno || "");
        setinvoicedate(
          inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
        );
        setguestname(inv.guestname || "");
        setgstno(inv.gstno || "");
        setcountry(inv.country || "");
        setamountinwords(inv.amountinwords || "");
        setSubtotal(inv.subtotal ?? "");
        setGst(inv.gst ?? "");
        setIncludeGst(Number(inv.gst) > 0);
        setTotal(inv.total ?? "");
        setRows(
          inv.items && inv.items.length > 0
            ? inv.items.map((item) => ({
                particulars: item.particulars || "",
                checkin: item.checkin ? String(item.checkin).split("T")[0] : "",
                checkout: item.checkout
                  ? String(item.checkout).split("T")[0]
                  : "",
                nights: item.nights ?? "",
                rate: item.rate ?? "",
                cgst: item.cgst ?? "",
                sgst: item.sgst ?? "",
              }))
            : [emptyRow()],
        );
      } catch (err) {
        console.error("Error loading invoice:", err);
        setLoadError("Something went wrong while loading the invoice.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  const addRow = () => {
    if (rows.length < 6) {
      setRows([...rows, emptyRow()]);
    }
  };

  const removeRow = (indexToRemove) => {
    setRows(rows.filter((_, index) => index !== indexToRemove));
  };

  const updateRow = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, i) => {
        if (i !== index) return row;

        let updated = { ...row, [field]: value };
        if (
          field === "checkin" &&
          updated.checkout &&
          updated.checkout <= updated.checkin
        ) {
          updated.checkout = "";
        }
        // Auto-calculate nights whenever either date changes
        if (field === "checkin" || field === "checkout") {
          const nights = calculateNights(updated.checkin, updated.checkout);
          if (nights !== null) {
            updated.nights = nights;
          }
        }

        // Auto-calculate CGST/SGST (9% each) whenever rate or nights changes
        if (
          field === "rate" ||
          field === "nights" ||
          field === "checkin" ||
          field === "checkout"
        ) {
          const gst = calculateRowGst(updated.rate, updated.nights);
          if (gst) {
            updated.cgst = gst.cgst;
            updated.sgst = gst.sgst;
          }
        }

        return updated;
      }),
    );
  };

  // Auto-calculate Sub Total, GST and Total whenever any row changes.
  // (Still plain editable inputs below — this just keeps them in sync
  // automatically; you can still overwrite them by hand if needed.)
  useEffect(() => {
    const newSubtotal = rows.reduce(
      (sum, r) => sum + (parseFloat(r.rate) || 0) * (parseFloat(r.nights) || 0),
      0,
    );
    // GST only counts when the invoice includes GST
    const newGst = includeGst
      ? rows.reduce(
          (sum, r) =>
            sum + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0),
          0,
        )
      : 0;
    const newTotal = newSubtotal + newGst;

    setSubtotal(newSubtotal ? newSubtotal.toFixed(2) : "");
    setGst(newGst ? newGst.toFixed(2) : "");
    setTotal(newTotal ? newTotal.toFixed(2) : "");
  }, [rows, includeGst]);

  // Keeps "In Words" in sync with Total, whether Total changed automatically
  // (from the rows effect above) or was typed in manually.
  useEffect(() => {
    const numericTotal = parseFloat(total) || 0;
    setamountinwords(numericTotal ? numberToWords(numericTotal) : "");
  }, [total]);

  const handleCancel = () => {
    const confirmed = window.confirm("Sure want to discard changes?");
    if (confirmed) {
      navigate("/Bills");
    }
  };

  const handleUpdate = async () => {
    if (!invoiceid) {
      alert("No invoice selected to update.");
      return;
    }
    if (!invoiceno || !invoicedate || !guestname) {
      alert("Invoice No, Invoice Date and Guest Name are required.");
      return;
    }
    if (rows.length === 0) {
      alert("Invoice must contain at least one item.");
      return;
    }

    const payload = {
      invoiceno,
      invoicedate,
      guestname,
      gstno: includeGst ? gstno : "",
      country,
      amountinwords,
      subtotal: subtotal || 0,
      gst: includeGst ? gst || 0 : 0,
      total: total || 0,
      items: rows.map((row) => ({
        particulars: row.particulars,
        checkin: row.checkin || null,
        checkout: row.checkout || null,
        nights: row.nights || 0,
        rate: row.rate || 0,
        cgst: includeGst ? row.cgst || 0 : 0,
        sgst: includeGst ? row.sgst || 0 : 0,
      })),
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.message || "Failed to update invoice.");
        return;
      }

      alert("Invoice updated successfully!");
      if (onUpdate) onUpdate(data);
      navigate("/bills");
    } catch (err) {
      console.error("Error updating invoice:", err);
      alert("Something went wrong while updating the invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shared input style (visual only)
  const bare =
    "w-full p-0 bg-transparent outline-none border-none focus:ring-0 focus:outline-none placeholder:text-[#b9bdb6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
  const label = "text-[10px] tracking-wide text-[#7b807a] uppercase";

  // grows the textarea to fit its content so long names wrap instead of being cut
  const autoResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-gray-600">
        Loading invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  return (
    <div className="cb-invoice max-w-4xl mx-auto px-12 py-12 bg-white text-[#1c2420] print:p-0">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
        .cb-invoice { font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif; }
        .cb-title { font-weight: 500; letter-spacing: -0.04em; line-height: 0.88; }
        .cb-invoice input, .cb-invoice textarea { font-family: inherit; }
        .cb-invoice table input, .cb-invoice table textarea { font-size: inherit; }
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* GST toggle (UI only, not printed) */}
      <label className="no-print mb-8 flex items-center gap-3 rounded-lg border border-[#d9dcd6] bg-[#f6f7f4] px-4 py-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={includeGst}
          onChange={(e) => setIncludeGst(e.target.checked)}
          className="h-4 w-4 rounded border-gray-400 text-[#1c2420] focus:ring-0 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-sm font-medium">
          Does this invoice include GST?
        </span>
        <span
          className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
            includeGst ? "bg-[#1c2420] text-white" : "bg-gray-200 text-gray-700"
          }`}
        >
          {includeGst ? "With GST" : "Without GST"}
        </span>
      </label>

      {/* Header */}
      <div className="flex justify-between items-start">
        <h1 className="cb-title text-[76px] uppercase">Invoice</h1>
        <div className="w-28 h-28 shrink-0">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mt-10 pb-6 border-b border-[#1c2420]">
        <div>
          <p className={label}>Invoice No.</p>
          <input
            type="text"
            className={`${bare} w-24 mt-1 text-sm font-bold`}
            placeholder="00"
            value={invoiceno}
            onChange={(e) => setinvoiceno(e.target.value)}
          />
        </div>
        <div>
          <p className={label}>Date</p>
          <DateField
            className={`${bare} mt-1 text-sm font-bold`}
            placeholder="Select date"
            display={formatDateLong(invoicedate)}
            value={invoicedate}
            onChange={(e) => setinvoicedate(e.target.value)}
          />
        </div>
      </div>

      {/* Bill To / From */}
      <div className="flex justify-between items-start py-6 border-b border-[#1c2420]">
        <div className="w-1/2">
          <p className={label}>Bill To</p>
          <input
            type="text"
            className={`${bare} mt-3 text-base font-bold`}
            placeholder="Guest Name"
            value={guestname}
            onChange={(e) => setguestname(e.target.value)}
          />
          {includeGst && (
            <div className="mt-1 flex items-center gap-1 text-xs tracking-wider text-[#3b413a]">
              <span className="shrink-0">GSTIN NO.:</span>
              <input
                type="text"
                maxLength={15}
                style={{ width: "auto" }}
                className={`${bare} flex-1 min-w-0 uppercase text-xs`}
                placeholder="15 character GSTIN"
                value={gstno}
                onChange={(e) =>
                  setgstno(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                  )
                }
              />
            </div>
          )}
          <textarea
            className={`${bare}  mt-1 text-xs tracking-wider text-[#3b413a] resize-none`}
            placeholder="Enter Address"
            value={country}
            onChange={(e) => setcountry(e.target.value)}
            rows={6}
            maxLength={350}
          />
        </div>
        <div className="text-right text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>From</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            Zorba Stays
          </p>
          {includeGst && <p>GSTIN NO.: 24AADFZ2976K1ZO</p>}
          <p>306-7 - Business Centrum,</p>
          <p>Amin Marg,</p>
          <p>Rajkot, 360001</p>
          <p>India</p>
        </div>
      </div>

      {/* Table */}
      <table className="w-full border-collapse text-xs mt-8">
        <thead>
          <tr className="text-[10px] uppercase text-[#7b807a] text-left align-bottom">
            <th className="pb-4 font-normal w-[50px]">
              Sr.
              {/* <br /> */}
              No.
            </th>
            <th
              className={`pb-4 font-normal ${includeGst ? "w-[24%]" : "w-[34%]"}`}
            >
              Particulars
            </th>
            <th
              className={`pb-4 font-normal ${includeGst ? "w-[28%]" : "w-[26%]"}`}
            >
              Period of stay
            </th>
            <th className="pb-4 font-normal text-center">No. of nights</th>
            <th className="pb-4 font-normal text-right">Per night rate</th>
            {includeGst && (
              <>
                <th className="pb-4 font-normal text-right">CGST</th>
                <th className="pb-4 font-normal text-right">SGST</th>
              </>
            )}
            <th className="pb-4 w-8 no-print"></th>
          </tr>
        </thead>
        <tbody className="border-t border-[#1c2420]">
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-[#1c2420] align-top">
              <td className="py-4">{index + 1}</td>
              <td className="py-4 pr-2">
                <textarea
                  rows={1}
                  ref={autoResize}
                  className={`${bare} block resize-none overflow-hidden leading-relaxed`}
                  placeholder="Villa Name"
                  value={row.particulars}
                  onChange={(e) =>
                    updateRow(index, "particulars", e.target.value)
                  }
                />
              </td>
              <td className="py-4 pr-2">
                <div className="flex items-center gap-2">
                  <DateField
                    wrapperClassName="w-[4.2rem] shrink-0"
                    className={`${bare} min-w-0 text-left`}
                    placeholder="DD-MM-YYYY"
                    display={formatDateToDisplay(row.checkin)}
                    value={row.checkin}
                    onChange={(e) =>
                      updateRow(index, "checkin", e.target.value)
                    }
                  />
                  <span className="mx-1">-</span>
                  <DateField
                    wrapperClassName="w-[4.2rem] shrink-0"
                    className={`${bare} min-w-0 text-left`}
                    placeholder="DD-MM-YYYY"
                    display={formatDateToDisplay(row.checkout)}
                    value={row.checkout}
                    min={nextDay(row.checkin)}
                    onChange={(e) => {
                      // ignore same-day or earlier dates (e.g. typed in by keyboard)
                      if (row.checkin && e.target.value <= row.checkin) return;
                      updateRow(index, "checkout", e.target.value);
                    }}
                  />
                </div>
              </td>
              <td className="py-4">
                <input
                  className={`${bare} text-center`}
                  placeholder="0"
                  value={row.nights}
                  onChange={(e) => updateRow(index, "nights", e.target.value)}
                />
              </td>
              <td className="py-4 pl-6">
                <div className="flex items-center justify-end">
                  <MoneyField
                    className={`${bare}`}
                    value={row.rate}
                    onChange={(e) => updateRow(index, "rate", e.target.value)}
                  />
                </div>
              </td>
              {includeGst && (
                <>
                  <td className="py-4 pl-6">
                    <div className="flex items-center justify-end">
                      <MoneyField
                        className={`${bare}`}
                        value={row.cgst}
                        onChange={(e) =>
                          updateRow(index, "cgst", e.target.value)
                        }
                      />
                    </div>
                  </td>
                  <td className="py-4 pl-6">
                    <div className="flex items-center justify-end">
                      <MoneyField
                        className={`${bare}`}
                        value={row.sgst}
                        onChange={(e) =>
                          updateRow(index, "sgst", e.target.value)
                        }
                      />
                    </div>
                  </td>
                </>
              )}
              <td className="py-4 no-print text-right">
                {index >= 1 && (
                  <button
                    onClick={() => removeRow(index)}
                    className="px-2 py-1 hover:scale-110"
                  >
                    <FiMinusCircle size={18} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Button */}
      <div className="my-2 no-print">
        <button
          onClick={addRow}
          disabled={rows.length >= 6}
          className={`py-2 ${rows.length >= 6 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
        >
          <GoPlusCircle size={22} />
        </button>
      </div>

      {/* Totals */}
      <div className="border-t border-[#1c2420] text-xs">
        {includeGst && (
          <>
            <div className="flex justify-end items-center py-3">
              <span className="mr-24">Sub Total</span>
              <div className="flex items-center justify-end w-28">
                <MoneyField
                  className={`${bare}`}
                  value={subtotal}
                  onChange={(e) => setSubtotal(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end items-center pb-3">
              <span className="mr-24">GST(18%)</span>
              <div className="flex items-center justify-end w-28">
                <MoneyField
                  className={`${bare}`}
                  value={gst}
                  onChange={(e) => setGst(e.target.value)}
                />
              </div>
            </div>
          </>
        )}
        <div
          className={`flex justify-end items-center py-4 font-bold border-b border-[#1c2420] ${
            includeGst ? "border-t" : ""
          }`}
        >
          <span className="mr-24">Total</span>
          <div className="flex items-center justify-end w-28">
            <MoneyField
              className={`${bare} font-bold`}
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* In Words */}
      <div className="mt-8">
        <p className={label}>In Words</p>
        <input
          type="text"
          className={`${bare} mt-1 text-sm font-bold`}
          placeholder="Enter amount in words"
          value={amountinwords}
          onChange={(e) => setamountinwords(e.target.value)}
        />
      </div>

      {/* Thank you */}
      <div className="mt-6 text-sm font-bold leading-snug">
        <p>Thank you!</p>
        <p>It’s been a pleasure hosting you!</p>
      </div>

      {/* Footer */}
      <div className="mt-14 flex items-center gap-3">
        <div className="flex-1 border-t border-[#1c2420]"></div>
        <img
          src="https://dash.zorbastays.com/assets/zorba_logo.webp"
          alt="Zorba Stays"
          className="w-6 h-6 object-contain"
        />
        <div className="flex-1 border-t border-[#1c2420]"></div>
      </div>
      <div className="mt-4 flex justify-between text-[10px] text-[#3b413a]">
        <span>@zorba_stays</span>
        <span>www.zorbastays.com</span>
      </div>

      <div className="flex justify-center gap-4 mt-10 mb-4 no-print">
        <button
          onClick={handleUpdate}
          disabled={isSubmitting}
          className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
            isSubmitting ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InvoiceEdit;
// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { GoPlusCircle } from "react-icons/go";
// import { FiMinusCircle } from "react-icons/fi";
// import {
//   calculateNights,
//   calculateRowGst,
//   numberToWords,
// } from "./invoiceUtils";
// import { API_URL } from "../../Config";

// const emptyRow = () => ({
//   particulars: "",
//   checkin: "",
//   checkout: "",
//   nights: "",
//   rate: "",
//   cgst: "",
//   sgst: "",
// });

// // invoiceid: the invoice to load and edit (falls back to the :invoiceid route param
// // so this works whether it's passed as a prop or rendered straight from a route)
// // onUpdate: optional callback fired with the server response after a successful save
// const InvoiceEdit = ({ invoiceid: invoiceidProp, onUpdate }) => {
//   const navigate = useNavigate();
//   const { invoiceid: invoiceidParam } = useParams();
//   const invoiceid = invoiceidProp || invoiceidParam;

//   const [rows, setRows] = useState([emptyRow()]);
//   const [invoiceno, setinvoiceno] = useState("");
//   const [invoicedate, setinvoicedate] = useState("");
//   const [guestname, setguestname] = useState("");
//   const [amountinwords, setamountinwords] = useState("");
//   const [subtotal, setSubtotal] = useState("");
//   const [gst, setGst] = useState("");
//   const [total, setTotal] = useState("");

//   const [isLoading, setIsLoading] = useState(true);
//   const [loadError, setLoadError] = useState(null);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const handleFocus = (e) => (e.target.type = "date");
//   const handleBlur = (e) => (e.target.type = "text");

//   // ---------------- Load existing invoice ----------------
//   useEffect(() => {
//     if (!invoiceid) {
//       setIsLoading(false);
//       return;
//     }

//     const fetchInvoice = async () => {
//       try {
//         setIsLoading(true);
//         setLoadError(null);
//         const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`);
//         const data = await res.json();

//         if (!res.ok || !data.success) {
//           setLoadError(data.message || "Failed to load invoice.");
//           return;
//         }

//         const inv = data.invoice;
//         setinvoiceno(inv.invoiceno || "");
//         setinvoicedate(
//           inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
//         );
//         setguestname(inv.guestname || "");
//         setamountinwords(inv.amountinwords || "");
//         setSubtotal(inv.subtotal ?? "");
//         setGst(inv.gst ?? "");
//         setTotal(inv.total ?? "");
//         setRows(
//           inv.items && inv.items.length > 0
//             ? inv.items.map((item) => ({
//                 particulars: item.particulars || "",
//                 checkin: item.checkin ? String(item.checkin).split("T")[0] : "",
//                 checkout: item.checkout
//                   ? String(item.checkout).split("T")[0]
//                   : "",
//                 nights: item.nights ?? "",
//                 rate: item.rate ?? "",
//                 cgst: item.cgst ?? "",
//                 sgst: item.sgst ?? "",
//               }))
//             : [emptyRow()],
//         );
//       } catch (err) {
//         console.error("Error loading invoice:", err);
//         setLoadError("Something went wrong while loading the invoice.");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchInvoice();
//   }, [invoiceid]);

//   const addRow = () => {
//     if (rows.length < 6) {
//       setRows([...rows, emptyRow()]);
//     }
//   };

//   const removeRow = (indexToRemove) => {
//     setRows(rows.filter((_, index) => index !== indexToRemove));
//   };

//   const updateRow = (index, field, value) => {
//     setRows((prevRows) =>
//       prevRows.map((row, i) => {
//         if (i !== index) return row;

//         let updated = { ...row, [field]: value };

//         // Auto-calculate nights whenever either date changes
//         if (field === "checkin" || field === "checkout") {
//           const nights = calculateNights(updated.checkin, updated.checkout);
//           if (nights !== null) {
//             updated.nights = nights;
//           }
//         }

//         // Auto-calculate CGST/SGST (9% each) whenever rate or nights changes
//         if (
//           field === "rate" ||
//           field === "nights" ||
//           field === "checkin" ||
//           field === "checkout"
//         ) {
//           const gst = calculateRowGst(updated.rate, updated.nights);
//           if (gst) {
//             updated.cgst = gst.cgst;
//             updated.sgst = gst.sgst;
//           }
//         }

//         return updated;
//       }),
//     );
//   };

//   // Auto-calculate Sub Total, GST and Total whenever any row changes.
//   // (Still plain editable inputs below — this just keeps them in sync
//   // automatically; you can still overwrite them by hand if needed.)
//   useEffect(() => {
//     const newSubtotal = rows.reduce(
//       (sum, r) => sum + (parseFloat(r.rate) || 0) * (parseFloat(r.nights) || 0),
//       0,
//     );
//     const newGst = rows.reduce(
//       (sum, r) => sum + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0),
//       0,
//     );
//     const newTotal = newSubtotal + newGst;

//     setSubtotal(newSubtotal ? newSubtotal.toFixed(2) : "");
//     setGst(newGst ? newGst.toFixed(2) : "");
//     setTotal(newTotal ? newTotal.toFixed(2) : "");
//   }, [rows]);

//   // Keeps "In Words" in sync with Total, whether Total changed automatically
//   // (from the rows effect above) or was typed in manually.
//   useEffect(() => {
//     const numericTotal = parseFloat(total) || 0;
//     setamountinwords(numericTotal ? numberToWords(numericTotal) : "");
//   }, [total]);

//   const handleCancel = () => {
//     const confirmed = window.confirm("Sure want to discard changes?");
//     if (confirmed) {
//       navigate("/Bills");
//     }
//   };

//   const handleUpdate = async () => {
//     if (!invoiceid) {
//       alert("No invoice selected to update.");
//       return;
//     }
//     if (!invoiceno || !invoicedate || !guestname) {
//       alert("Invoice No, Invoice Date and Guest Name are required.");
//       return;
//     }
//     if (rows.length === 0) {
//       alert("Invoice must contain at least one item.");
//       return;
//     }

//     const payload = {
//       invoiceno,
//       invoicedate,
//       guestname,
//       amountinwords,
//       subtotal: subtotal || 0,
//       gst: gst || 0,
//       total: total || 0,
//       items: rows.map((row) => ({
//         particulars: row.particulars,
//         checkin: row.checkin || null,
//         checkout: row.checkout || null,
//         nights: row.nights || 0,
//         rate: row.rate || 0,
//         cgst: row.cgst || 0,
//         sgst: row.sgst || 0,
//       })),
//     };

//     try {
//       setIsSubmitting(true);
//       const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`, {
//         method: "PUT",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       const data = await res.json();

//       if (!res.ok || !data.success) {
//         alert(data.message || "Failed to update invoice.");
//         return;
//       }

//       alert("Invoice updated successfully!");
//       if (onUpdate) onUpdate(data);
//       navigate("/bills");
//     } catch (err) {
//       console.error("Error updating invoice:", err);
//       alert("Something went wrong while updating the invoice.");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (isLoading) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         Loading invoice...
//       </div>
//     );
//   }

//   if (loadError) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
//         {loadError}
//       </div>
//     );
//   }

//   return (
//     <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
//       <style>{`
//         @media print {
//           @page { margin: 0; }
//           body { margin: 1.6cm; }
//           .no-print { display: none; }
//         }
//       `}</style>

//       {/* Header */}
//       <div className="flex justify-between items-start mb-6">
//         <div>
//           <h1 className="text-4xl font-bold mb-2">Invoice</h1>
//           <br />
//           <br />
//           <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
//           <p className="text-sm">
//             306, 7 - Business Centrum, <br /> Amin Marg, Rajkot, 360001
//           </p>
//           {/* <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p> */}
//         </div>
//         <div className="w-24 h-24">
//           <img
//             src="https://zorbastays.com/android-chrome-512x512.png"
//             alt="Zorba Stays Logo"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       <div className="border-t border-black mb-6"></div>

//       {/* Invoice Meta */}
//       <div className="flex gap-16 mb-8">
//         <div className="flex flex-col gap-1">
//           <p className="text-xs font-bold uppercase">Invoice No.</p>
//           <input
//             type="text"
//             value={invoiceno}
//             onChange={(e) => setinvoiceno(e.target.value)}
//             className="w-20 p-0 border-0 bg-transparent outline-none focus:ring-0"
//           />
//         </div>
//         <div className="flex flex-col gap-1">
//           <p className="text-xs font-bold uppercase m-0">Invoice Date</p>
//           <input
//             type="date"
//             value={invoicedate}
//             onChange={(e) => setinvoicedate(e.target.value)}
//             className="p-0 border-0 bg-transparent outline-none focus:ring-0"
//             // onFocus={handleFocus}
//             // onBlur={handleBlur}
//           />
//         </div>
//       </div>

//       <div className="mb-8">
//         <p className="font-bold">Invoice To:-</p>
//         <input
//           type="text"
//           value={guestname}
//           onChange={(e) => setguestname(e.target.value)}
//           className="w-full text-lg p-0 leading-tight border-0 bg-transparent outline-none focus:ring-0"
//         />
//       </div>

//       {/* Table Design */}
//       <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
//         <thead>
//           <tr className="uppercase text-xs font-bold border-b border-black">
//             <th className="py-3  border-black w-[60px]">Sr. No.</th>
//             <th className="py-3">Particulars</th>
//             <th className="py-3">Period of stay</th>
//             <th className="py-3">No. Nights</th>
//             <th className="py-3">Per Night Rate</th>
//             <th className="py-3">CGST</th>
//             <th className="py-3">SGST</th>
//           </tr>
//         </thead>
//         <tbody>
//           {rows.map((row, index) => (
//             <tr key={index} className="border-b border-black">
//               <td className="py-3  border-black">{index + 1}</td>
//               <td className="py-3 px-2">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.particulars}
//                   onChange={(e) =>
//                     updateRow(index, "particulars", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3 px-2 flex justify-center items-center gap-1">
//                 <input
//                   type="date"
//                   className="w-full text-center  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                   value={row.checkin}
//                   onChange={(e) => updateRow(index, "checkin", e.target.value)}
//                   // onFocus={handleFocus}
//                   // onBlur={handleBlur}
//                   placeholder="DD-MM-YYYY"
//                 />
//                 <span>to</span>
//                 <input
//                   type="date"
//                   className="w-full text-center  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                   value={row.checkout}
//                   onChange={(e) => updateRow(index, "checkout", e.target.value)}
//                   // onFocus={handleFocus}
//                   // onBlur={handleBlur}
//                   placeholder="DD-MM-YYYY"
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.nights}
//                   onChange={(e) => updateRow(index, "nights", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.rate}
//                   onChange={(e) => updateRow(index, "rate", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.cgst}
//                   onChange={(e) => updateRow(index, "cgst", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.sgst}
//                   onChange={(e) => updateRow(index, "sgst", e.target.value)}
//                 />
//               </td>
//               <td className="py-3 no-print">
//                 {index >= 1 && (
//                   <button
//                     onClick={() => removeRow(index)}
//                     className="px-2 py-1 hover:scale-110"
//                   >
//                     <FiMinusCircle size={18} />
//                   </button>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Add Button */}
//       <div className="my-2 no-print">
//         <button
//           onClick={addRow}
//           disabled={rows.length >= 6}
//           className={`px-4 py-2 ${rows.length >= 6 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
//         >
//           <GoPlusCircle size={22} />
//         </button>
//       </div>

//       {/* Totals (Design preserved) */}
//       <div className="flex justify-between items-start mt-6 px-2">
//         <div className="w-1/2">
//           <p className="font-bold mb-1">In Words:</p>
//           <input
//             type="text"
//             value={amountinwords}
//             onChange={(e) => setamountinwords(e.target.value)}
//             className="
//     w-full
//     uppercase
//     p-0
//     m-0
//     h-7
//     leading-none
//     border-0
//     border-b
//     border-black
//     bg-transparent
//     rounded-none
//     focus:ring-0
//     focus:border-black
//   "
//           />
//         </div>
//         <div className="w-72">
//           <div className="flex justify-between py-1">
//             <span className="font-bold">Sub Total</span>
//             <input
//               type="text"
//               value={subtotal}
//               onChange={(e) => setSubtotal(e.target.value)}
//               className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-1 border-t border-black">
//             <span className="font-bold">GST</span>
//             <input
//               type="text"
//               value={gst}
//               onChange={(e) => setGst(e.target.value)}
//               className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-2 font-bold border-t border-black mt-1">
//             <span>Total</span>
//             <input
//               type="text"
//               value={total}
//               onChange={(e) => setTotal(e.target.value)}
//               className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-2 font-bold border-t border-black"></div>
//         </div>
//       </div>

//       {/* Footer */}
//       <div className="mt-20 text-center text-xs border-t border-black pt-8">
//         <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
//       </div>

//       {/* Update/Cancel Buttons */}
//       <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
//         <button
//           onClick={handleUpdate}
//           disabled={isSubmitting}
//           className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
//             isSubmitting ? "opacity-60 cursor-not-allowed" : ""
//           }`}
//         >
//           {isSubmitting ? "Updating..." : "Update"}
//         </button>
//         <button
//           onClick={handleCancel}
//           className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//         >
//           Cancel
//         </button>
//       </div>
//     </div>
//   );
// };

// export default InvoiceEdit;
